import { useEffect, useRef, useState, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

export interface CinematicHeroProps {
  /** Primary backdrop/still URL (already chosen by the shared `imageUrls()`). */
  backdropUrl?: string;
  /** Second origin, tried once when the primary errors. */
  fallbackUrl?: string;
  /** The title's two palette colours — placeholder wash and error fallback. */
  palette: [string, string];
  /**
   * The work's name; set in `text-headline` over the palette when both image
   * origins fail — never a broken-image icon (ui-ux.md §6).
   */
  title: string;
  /** Opt out of the error-fallback title when `children` already carry it. */
  fallbackTitle?: boolean;
  /** Title/meta/actions rendered over the scrim along the bottom edge. */
  children?: ReactNode;
  /**
   * `fade` (default): a full-height fade to the canvas — the detail hero.
   * `deep`: solid canvas over the bottom half, fading out above it — for a
   * card whose overlaid copy runs several lines (Discover's spotlight), so
   * every line sits on ≥ 80% canvas whatever the backdrop.
   * `hero`: Tonight's "now showing" band (`.scrim-hero`, index.css) — below
   * `sm` a plain bottom fade (the copy sits below the image); from `sm` one
   * left-to-right wash (~85% canvas behind the copy column, ~10% by 60%
   * width at `lg`) plus a short bottom fade, so the image reads across the
   * band while bottom-left copy stays AA.
   * `detail`: the title page's hero (`.scrim-detail`) — the `hero` recipe
   * with a wider hold for its longer copy column (and the lg+ cover).
   */
  scrim?: 'fade' | 'deep' | 'hero' | 'detail';
  /** Height overrides (default `h-[42vh] min-h-[280px] max-h-[480px]`). */
  className?: string;
}

/**
 * The one cinematic hero (ui-ux.md §6): a full-bleed backdrop box with the
 * same primary → fallback → failed image chain as `Poster`, a bottom gradient
 * scrim to the canvas colour so overlaid copy sits on solid contrast, and the
 * palette gradient underneath from the first paint. A show detail page and an
 * episode-discussion page are two call sites of THIS component — the backdrop
 * image itself is decorative (`alt=""`); the identity comes from the caller's
 * overlaid `children`.
 *
 * Apply the page-gutter bleed (`-mx-4 sm:-mx-6 lg:-mx-8`) and any height
 * override via `className` at the call site.
 */
export function CinematicHero({
  backdropUrl,
  fallbackUrl,
  palette,
  title,
  fallbackTitle = true,
  scrim = 'fade',
  children,
  className,
}: CinematicHeroProps) {
  const [src, setSrc] = useState<string | undefined>(backdropUrl);
  const [phase, setPhase] = useState<'loading' | 'ready' | 'error'>(
    backdropUrl ? 'loading' : 'error',
  );

  const imgRef = useRef<HTMLImageElement | null>(null);
  const shownUrl = useRef(backdropUrl);

  useEffect(() => {
    // Same fix as `Poster`: reset only when the URL really changes. A cached
    // image can fire `load` before this effect runs, and an unconditional
    // reset left it stuck at opacity 0. An already-decoded image is ready.
    if (shownUrl.current !== backdropUrl) {
      shownUrl.current = backdropUrl;
      setSrc(backdropUrl);
      setPhase(backdropUrl ? 'loading' : 'error');
      return;
    }
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) setPhase('ready');
  }, [backdropUrl]);

  const handleError = () => {
    if (src && fallbackUrl && src !== fallbackUrl) {
      setSrc(fallbackUrl);
      return;
    }
    setPhase('error');
  };

  return (
    <div
      className={cn('relative h-[42vh] max-h-[480px] min-h-[280px] overflow-hidden', className)}
      style={{ background: `linear-gradient(160deg, ${palette[0]} 0%, ${palette[1]} 100%)` }}
    >
      {src && phase !== 'error' && (
        <img
          ref={imgRef}
          src={src}
          alt=""
          decoding="async"
          onLoad={() => setPhase('ready')}
          onError={handleError}
          className={cn(
            'absolute inset-0 h-full w-full object-cover transition-opacity duration-150',
            phase === 'ready' ? 'opacity-100' : 'opacity-0',
          )}
        />
      )}
      {/* Bottom scrim to the canvas colour — overlaid text sits on solid contrast. */}
      <div
        aria-hidden
        className={cn(
          'absolute inset-0',
          scrim === 'deep'
            ? 'bg-gradient-to-t from-bg from-50% to-transparent to-95%'
            : scrim === 'hero'
              ? 'scrim-hero'
              : scrim === 'detail'
                ? 'scrim-detail'
                : 'bg-gradient-to-b from-transparent to-bg',
        )}
      />
      <div className="absolute inset-0 flex flex-col justify-end gap-2 p-4 sm:p-6">
        {phase === 'error' && fallbackTitle && (
          <p className="text-headline text-ink">{title}</p>
        )}
        {children}
      </div>
    </div>
  );
}
